import moduleManager from './ModuleManager.js'

class RoutingModule {
  constructor() {
    this.name = 'routing'
    this.isEnabled = false
    this.routingState = {
      currentRoute: null,
      availableRoutes: [],
      navigationEnabled: false
    }
  }

  // Called when module is enabled
  onEnable(tourSettings) {
    console.log('[RoutingModule] Routing module enabled')
    this.isEnabled = true
    
    // Initialize routing state from tour settings
    if (tourSettings.navigation?.routing) {
      this.routingState = {
        currentRoute: null,
        availableRoutes: tourSettings.navigation.routing.routes || [],
        navigationEnabled: tourSettings.navigation.routing.active || false
      }
    }
    
    // Register event listeners
    this.registerEventListeners()
    
    // Store initial routing state
    moduleManager.setModuleData('routing', this.routingState)
  }

  // Called when module is disabled
  onDisable() {
    console.log('[RoutingModule] Routing module disabled')
    this.isEnabled = false
    
    // Remove event listeners
    this.removeEventListeners()
    
    // Clear current route
    this.routingState.currentRoute = null
    moduleManager.setModuleData('routing', this.routingState)
  }

  registerEventListeners() {
    // Listen for point opening events to enable navigation
    moduleManager.addEventListener('point:opening', this.handlePointOpening.bind(this), 'routing')
    
    // Listen for location updates
    moduleManager.addEventListener('location:updated', this.handleLocationUpdate.bind(this), 'routing')
    
    // Listen for navigation requests
    moduleManager.addEventListener('navigation:request', this.handleNavigationRequest.bind(this), 'routing')
  }

  removeEventListeners() {
    moduleManager.removeEventListener('point:opening', this.handlePointOpening.bind(this), 'routing')
    moduleManager.removeEventListener('location:updated', this.handleLocationUpdate.bind(this), 'routing')
    moduleManager.removeEventListener('navigation:request', this.handleNavigationRequest.bind(this), 'routing')
  }

  // Handle point opening - enable navigation features
  handlePointOpening(data) {
    const { pointId, pointData } = data
    
    if (this.routingState.navigationEnabled) {
      console.log(`[RoutingModule] Enabling navigation for point ${pointId}`)
      
      // Enable navigation features for this point
      moduleManager.dispatchEvent('routing:navigation_enabled', {
        pointId,
        pointData,
        availableRoutes: this.routingState.availableRoutes
      })
    }
  }

  // Handle location updates
  handleLocationUpdate(data) {
    const { latitude, longitude, accuracy } = data
    
    if (this.routingState.currentRoute) {
      console.log(`[RoutingModule] Updating route with new location: ${latitude}, ${longitude}`)
      
      // Update current route with new location
      this.updateRouteProgress(latitude, longitude)
      
      // Dispatch route update event
      moduleManager.dispatchEvent('routing:route_updated', {
        currentRoute: this.routingState.currentRoute,
        location: { latitude, longitude, accuracy }
      })
    }
  }

  // Handle navigation requests
  handleNavigationRequest(data) {
    const { destination, routeType = 'walking' } = data
    
    if (!this.routingState.navigationEnabled) {
      console.log('[RoutingModule] Navigation is disabled')
      return false
    }
    
    console.log(`[RoutingModule] Starting navigation to ${destination} via ${routeType}`)
    
    // Start navigation
    this.startNavigation(destination, routeType)
    
    return true
  }

  // Start navigation to a destination
  startNavigation(destination, routeType) {
    // This would integrate with the actual routing API
    const route = {
      id: `route_${Date.now()}`,
      destination,
      routeType,
      startTime: new Date(),
      status: 'active'
    }
    
    this.routingState.currentRoute = route
    
    // Update module data
    moduleManager.setModuleData('routing', this.routingState)
    
    // Dispatch navigation started event
    moduleManager.dispatchEvent('routing:navigation_started', {
      route,
      destination,
      routeType
    })
  }

  // Update route progress based on current location
  updateRouteProgress(latitude, longitude) {
    if (!this.routingState.currentRoute) return
    
    // This would calculate progress based on distance to destination
    const progress = this.calculateProgress(latitude, longitude)
    
    this.routingState.currentRoute.progress = progress
    
    // Check if destination reached
    if (progress >= 100) {
      this.completeNavigation()
    }
  }

  // Calculate progress to destination
  calculateProgress(latitude, longitude) {
    // This is a simplified calculation
    // In reality, you'd calculate actual distance to destination
    return Math.random() * 100 // Placeholder
  }

  // Complete navigation
  completeNavigation() {
    if (!this.routingState.currentRoute) return
    
    console.log(`[RoutingModule] Navigation completed to ${this.routingState.currentRoute.destination}`)
    
    const completedRoute = {
      ...this.routingState.currentRoute,
      endTime: new Date(),
      status: 'completed'
    }
    
    // Dispatch navigation completed event
    moduleManager.dispatchEvent('routing:navigation_completed', {
      route: completedRoute
    })
    
    // Clear current route
    this.routingState.currentRoute = null
    moduleManager.setModuleData('routing', this.routingState)
  }

  // Public methods for other components to use
  isNavigationEnabled() {
    return this.routingState.navigationEnabled
  }

  getCurrentRoute() {
    return this.routingState.currentRoute
  }

  getAvailableRoutes() {
    return this.routingState.availableRoutes
  }

  // Request navigation to a point
  navigateToPoint(pointId, routeType = 'walking') {
    if (!this.isNavigationEnabled()) {
      console.log('[RoutingModule] Navigation is disabled')
      return false
    }
    
    // Find point coordinates (this would come from point data)
    const destination = this.getPointCoordinates(pointId)
    
    if (destination) {
      return this.startNavigation(destination, routeType)
    }
    
    return false
  }

  // Get point coordinates (placeholder)
  getPointCoordinates(pointId) {
    // This would fetch actual coordinates from point data
    return { latitude: 52.3676, longitude: 4.9041 } // Example coordinates
  }
}

// Create and register the module
const routingModule = new RoutingModule()
moduleManager.registerModule('routing', routingModule)

export default routingModule 